<template>
  <h3>Team</h3>
  <div v-if="organizations.length >= 1" class="form-group">
    <OrganizationSelect v-bind="$attrs" :organizations="organizations" :organization-id="organizationId" />
  </div>
  <div id="user_form" class="form-group">
    <h4>
      <span>{{ editId != null ? "Edit" : "Add" }} Team Member</span>
      <span v-if="editId != null"> &middot; <a @click="reset">Cancel</a></span>
    </h4>
    <p class="infomsg">You can add multiple users to your organization. Super admins have access to settings, reports and all trips. Trip admins have access to all trips within the organization. Standard users have access to create new trips and only view their own trips.</p>
    <div class="form" :class="{ loading: formIsLoading }">
      <Result v-model:error="formError" v-model:is-success="formIsSuccess" />
      <div class="form-section">
        <div class="form-row">
          <input v-model="formCustomerName" type="text" maxlength="255" required placeholder="Full name" :disabled="editId != null" />
        </div>
        <div class="form-row">
          <input v-model="formCustomerEmail" type="text" maxlength="100" required placeholder="Email address" :disabled="editId != null" />
        </div>
        <div class="form-row">
          <select v-model="formUserType" required>
            <option :value="0">Standard</option>
            <option :value="2">Trip Admin</option>
            <option :value="1">Super Admin</option>
          </select>
        </div>
        <div v-if="formPolicies && formPolicies.length > 0" class="form-row">
          <select v-model="formPolicyId" required>
            <option :value="null">Select a policy</option>
            <option v-for="(p, i) in formPolicies" :key="i" :value="p.id">
              {{ p.title }}
            </option>
          </select>
        </div>
      </div>
      <button @click="save">Save Team Member</button>
    </div>
  </div>
  <div class="form-group">
    <h4>Team List</h4>
    <table :class="{ loading: tableIsLoading }">
      <thead>
        <tr>
          <th>Name</th>
          <th>Role</th>
          <th>Policy</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="tableMembers.length == 0">
          <td colspan="4">No results</td>
        </tr>
        <tr v-for="m in tableMembers" v-else :key="m.id">
          <td>{{ m.customer_name }}</td>
          <td>
            {{ getUserRole(m.user_type) }}
          </td>
          <td>
            {{ m.policy_title ? m.policy_title : "NA" }}
          </td>
          <td class="actions">
            <a @click="actionEdit(m.id)">Edit</a>
            <a v-if="m.customer_id != userId && organizationId != 5433" @click="actionDelete(m.id)">Delete</a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, inject } from "vue";
import Result from "../components/common/FormResult.vue";
import OrganizationSelect from "../components/common/OrganizationSelect.vue";
import useApi from "../utils/apiCalls.js";

const props = defineProps(["organizations", "organizationId"]);
const emit = defineEmits(["error", "info", "scroll"]);

const userId = inject("userId");

// Reactive State
const editId = ref(null);
const formCustomerName = ref(null);
const formCustomerEmail = ref(null);
const formPolicyId = ref(null);
const formPolicies = ref([]);
const formUserType = ref(0);
const formIsLoading = ref(false);
const formIsSuccess = ref(false);
const formError = ref(null);
const tableIsLoading = ref(true);
const tableMembers = ref([]);

// API Utilities
const { apiGetRequest, apiDeleteRequest, apiPutRequest, apiPostRequest } = useApi(emit);

// Watchers
watch(editId, newValue => {
  if (newValue != null) {
    const el = document.getElementById("user_form");
    el.scrollIntoView({ behavior: "smooth" });
    loadUser();
  }
});

watch(
  () => props.organizationId,
  () => {
    console.log("organizationId changed");
    load();
    loadPolicies();
  }
);

// Lifecycle Hooks
onMounted(() => {
  if (props.organizationId != null) {
    load();
    loadPolicies();
  }
});

// Methods
const getUserRole = role => {
  const roles = {
    0: "Standard",
    1: "Super Admin",
    2: "Trip Admin",
  };
  return roles[role] || "Unknown"; // Default to "Unknown" if the role is not recognized
};

const actionDelete = id => {
  tableIsLoading.value = true;

  apiDeleteRequest({
    path: `/organizations/${props.organizationId}/members/${id}`,
    onSuccess: () => {
      tableIsLoading.value = false;
      load();
      reset();
    },
    onError: error => {
      tableIsLoading.value = false;
      emit("error", error);
    },
  });
};

const actionEdit = id => {
  formIsSuccess.value = false;
  formError.value = null;
  editId.value = id;
};

const load = () => {
  tableIsLoading.value = true;

  apiGetRequest({
    path: `/organizations/${props.organizationId}/members`,
    onSuccess: response => {
      tableMembers.value = response;
      tableIsLoading.value = false;
    },
    onError: error => {
      tableIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadPolicies = () => {
  apiGetRequest({
    path: `/organizations/${props.organizationId}/policies`,
    onSuccess: response => {
      formPolicies.value = response;
    },
    onError: error => {
      emit("error", error);
    },
  });
};

const loadUser = () => {
  formIsLoading.value = true;

  apiGetRequest({
    path: `/organizations/${props.organizationId}/members/${editId.value}`,
    onSuccess: response => {
      formCustomerName.value = response.customer_name;
      formCustomerEmail.value = response.customer_email;
      formPolicyId.value = response.policy_id;
      formUserType.value = response.user_type;
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const reset = () => {
  editId.value = null;
  formCustomerName.value = null;
  formCustomerEmail.value = null;
  formUserType.value = 0;
};

const save = () => {
  formIsLoading.value = true;

  const url = editId.value != null ? `/organizations/${props.organizationId}/members/${editId.value}` : `/organizations/${props.organizationId}/members`;

  const method = editId.value != null ? apiPutRequest : apiPostRequest;

  method({
    path: url,
    data: {
      customer_name: formCustomerName.value,
      customer_email: formCustomerEmail.value,
      policy_id: formPolicyId.value,
      user_type: formUserType.value,
    },
    onSuccess: () => {
      reset();
      formIsSuccess.value = true;
      formError.value = null;
      formIsLoading.value = false;
      load();
    },
    onError: error => {
      formIsSuccess.value = false;
      formError.value = error;
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};
</script>
